<template>
  <div class="app container-fluid i-px-0">
    <preloader-component></preloader-component>
    <cookie-consent
      v-if="cookieConsentBannerShouldBeShown"
      :banner-visible="cookieBannerVisible"
    ></cookie-consent>
    <router-view
      v-if="canViewPublicRoutes || canViewAuthenticatedRoutes"
      @open-cookie-manager="openCookieManager"
    ></router-view>
    <keyboard-shortcut-listeners></keyboard-shortcut-listeners>
    <chat-panel></chat-panel>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import Pusher from 'pusher-js';
import { showDocumentProcessedNotification } from '@/utils/notifications';
import CookieConsent from '@/V2/CookieConsent';
import config from '@/config';
import '@inscribe/signal/style.css';
import { Intercom } from '@intercom/messenger-js-sdk';
import KeyboardShortcutListeners from '@/V2/navigation/KeyboardShortcutListeners.vue';
import { notifyErrorToUI } from '@/utils/error';

// components
import Preloader from '@/V2/Preloader.vue';
import ChatPanel from '@/V2/chat/ChatPanel.vue';

// constants
import { LOCAL_STORAGE_REDIRECT_URL } from '@/constants/login';
import { BASE_URL, ENV, isProduction } from './environment';

// services
import ApiService from '@/shared/services/api.service';
import { doRedirect } from '@/utils/auth';

const CONFIG = config[ENV];
export default {
  name: 'App',
  components: {
    CookieConsent,
    'preloader-component': Preloader,
    ChatPanel,
    KeyboardShortcutListeners,
  },
  data() {
    return {
      message: null,
      success: null,
      broadcastChannel: null,
      cookieBannerVisible: false,
      intercomInitialized: false,
    };
  },
  computed: {
    ...mapState(['currentUser']),
    ...mapGetters(['isIntercomChatEnabled']),
    cookieConsentBannerShouldBeShown() {
      return isProduction;
    },
    canViewPublicRoutes() {
      return this.$route.path.startsWith('/auth') || this.$route.name === '404';
    },
    canViewAuthenticatedRoutes() {
      return (
        this.$store.state.hasUserCompletedRegistration &&
        !!this.$store.state.featureFlagsStore.featureFlags
      );
    },
    shouldInitializeIntercom() {
      return (
        isProduction &&
        this.currentUser &&
        this.isIntercomChatEnabled &&
        !this.intercomInitialized
      );
    },
  },
  watch: {
    shouldInitializeIntercom(newVal) {
      if (newVal) {
        this.initializeIntercom(this.currentUser);
      }
    },
  },
  mounted() {
    this.setupBroadcastChannel();

    const redirectUrl = localStorage.getItem(LOCAL_STORAGE_REDIRECT_URL);
    if (redirectUrl) {
      localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URL);
      doRedirect(redirectUrl);
    }

    ApiService.getAndSetCsrfToken()
      .then(() => this.getCurrentUser(CONFIG))
      .then((user) => {
        this.emitter.emit('preloader.off');
        this.maybeForceOtherTabRefresh(user.organisation.id);
        this.setupMostRecentWorkspace(user.organisation.id);
        this.connectToPusher(user, ApiService.token);
        const userObj = {
          id: user.uuid,
          email: user.email,
          organisation: user.organisation.name,
          screenSize: {
            height: window.innerHeight,
            width: window.innerWidth,
          },
        };

        if (isProduction) {
          Sentry.getCurrentScope().setUser(userObj);
        }
        this.getTags();
        this.getWorkspaces();
      })
      .catch((error) => {
        this.emitter.emit('preloader.off');
        // eslint-disable-next-line no-console
        console.error(error);
      });

    this.emitter.on('document-state-change', (pusherData) => {
      if (
        this.$store.state.customers.selectedCustomer &&
        pusherData.customer_id ===
          this.$store.state.customers.selectedCustomer.id
      ) {
        // refetch cached documents with style=summary for this customer
        this.fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID(
          pusherData.customer_id,
        );
        this.$store.commit(
          'invalidateDocumentCacheByID',
          pusherData.customer_id,
        );
        // refetch the affected document
        this.getDocumentWithSignals({
          customerId: pusherData.customer_id,
          documentId: pusherData.document_id,
        }).then(({ data }) => {
          if (this.$store.state.currentUser.browser_notifications_enabled) {
            this.showDocumentNotification(data);
          }

          this.$store.commit('deleteUploadingDocWithName', {
            name: data.name,
          });

          this.$store.commit('updateGeneric', {
            objectsName: 'documents',
            updatedObject: {
              id: pusherData.document_id,
              ...data,
            },
          });

          if (data.is_fraudulent && this.$store.state.documentsMetadata) {
            const { fraudResults, pagination } =
              this.$store.state.documentsMetadata;
            this.$store.commit('setDocumentsMetadata', {
              pagination,
              fraudResults: {
                fraudulent_count: fraudResults.fraudulent_count + 1,
              },
            });
          }
        });
      } else if (this.$store.state.currentUser.browser_notifications_enabled) {
        this.getDocument({
          customerId: pusherData.customer_id,
          documentId: pusherData.document_id,
        }).then(({ data }) => {
          this.showDocumentNotification(data);
        });
      }
    });
    this.emitter.on('customer-fraud-rating-updated', (pusherData) => {
      if (
        this.$store.state.customers.selectedCustomer &&
        pusherData.customer_id ===
          this.$store.state.customers.selectedCustomer.id
      ) {
        this.$store.commit('updateSelectedCustomer', {
          fraud_rating: pusherData.fraud_rating,
        });
      }
    });
    /*
     Note: break these agent-related pusher configs into separate files soon.
     Context: https://github.com/InscribeAI/frontend/pull/1157#discussion_r1651044874
    */
    this.emitter.on('document-review-agent-status-updated', (pusherData) => {
      if (
        this.$route.params.document_id === pusherData.document_id.toString()
      ) {
        this.fetchAndCacheDocumentReviewAgentRunsForDocId({
          documentId: pusherData.document_id,
          customerUUID: pusherData.customer_uuid,
        });
      }
      if (
        this.$route.params.customer_id === pusherData.customer_uuid.toString()
      ) {
        this.fetchAndCacheDocumentReviewAgentRunsForCustomerUUID(
          pusherData.customer_uuid,
        );
      }
    });

    this.emitter.on('customer-review-agent-status-updated', (pusherData) => {
      if (
        this.$route.params.customer_id === pusherData.customer_uuid.toString()
      ) {
        // Refetch all customer-related data
        this.fetchAndCacheCustomer(pusherData.customer_uuid);
        this.fetchAndCacheFraudSummary({
          customerId: pusherData.customer_uuid,
        });
        this.fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID(
          pusherData.customer_uuid,
        );
        this.fetchAndCacheCustomerReviewAgentRunsForCustomerUUID(
          pusherData.customer_uuid,
        );
        this.fetchAndCacheDocumentReviewAgentRunsForCustomerUUID(
          pusherData.customer_uuid,
        );
        this.fetchAndCacheBusinessComplianceAgentRunsForCustomerUUID(
          pusherData.customer_uuid,
        );
        this.fetchAndCacheCustomerSignalsForCustomerUUID({
          customerId: pusherData.customer_uuid,
        });
      }
    });

    this.emitter.on(
      'customer-fraud-reasoning-agent-run-updated',
      (pusherData) => {
        if (
          this.$route.params.customer_id === pusherData.customer_uuid.toString()
        ) {
          this.fetchAndCacheCustomer(pusherData.customer_uuid);
        }
      },
    );

    this.emitter.on(
      'business-compliance-agent-status-updated',
      (pusherData) => {
        if (
          this.$route.params.customer_id === pusherData.customer_uuid.toString()
        ) {
          this.fetchAndCacheBusinessComplianceAgentRunsForCustomerUUID(
            pusherData.customer_uuid,
          );
        }
      },
    );

    axios.interceptors.request.use(null, function (error) {
      return Promise.reject(error);
    });

    axios.interceptors.response.use(null, (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      const status = error?.response?.status;
      notifyErrorToUI(error);
      if (!document.location.hash.includes('/auth/') && status === 401) {
        const redirectUrl = encodeURI(document.location.hash);
        const auth_required = error?.response?.data?.data?.auth_required;
        if (auth_required === '2fa') {
          this.$router.push({
            name: 'await-2fa',
            query: {
              redirectUrl,
              type: error?.response?.data?.data?.two_fa_type,
            },
          });
        } else {
          this.$router.push({ name: 'login', query: { redirectUrl } });
        }
      }
      return Promise.reject(error);
    });
  },
  methods: {
    ...mapActions([
      'getWorkspaces',
      'getTags',
      'getCurrentUser',
      'getDocumentWithSignals',
      'getDocument',
      'fetchAndCacheCustomer',
      'fetchAndCacheFraudSummary',
      'fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID',
      'fetchAndCacheDocumentReviewAgentRunsForDocId',
      'fetchAndCacheDocumentReviewAgentRunsForCustomerUUID',
      'fetchAndCacheCustomerReviewAgentRunsForCustomerUUID',
      'fetchAndCacheBusinessComplianceAgentRunsForCustomerUUID',
      'fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID',
      'fetchAndCacheCustomerSignalsForCustomerUUID',
    ]),
    ...mapMutations(['setCurrentDocumentReviewAgentRun']),
    openCookieManager() {
      this.cookieBannerVisible = true;
    },
    setupBroadcastChannel() {
      // Protect against older browsers without this API
      if ('BroadcastChannel' in window) {
        const channel = new BroadcastChannel('inscribe');

        channel.addEventListener('message', (event) => {
          const message = event.data;

          if (message.type === 'WORKSPACE_CHANGE') {
            // Workspace has changed elsewhere, need to reload the app to maintain a
            // consistent view of the data that can be loaded with the current session cookie
            window.location.href = '/';
          }
        });

        this.broadcastChannel = channel;
      }
    },
    maybeForceOtherTabRefresh(authenticatedOrganisationId) {
      if (this.broadcastChannel) {
        const previousId = localStorage.getItem('mostRecentWorkspace');
        if (previousId && previousId !== authenticatedOrganisationId) {
          // We've logged in to a different workspace than before
          // Inform other tabs running the app to reload
          this.broadcastChannel.postMessage({
            type: 'WORKSPACE_CHANGE',
            data: {},
          });
        }
      }
    },
    setupMostRecentWorkspace(organisationId) {
      localStorage.setItem('mostRecentWorkspace', organisationId);
    },
    connectToPusher(user, csrfToken) {
      Pusher.Runtime.createXHR = function () {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        return xhr;
      };
      const pusher = new Pusher(CONFIG.pusher.key, {
        cluster: CONFIG.pusher.cluster,
        forceTLS: true,
        channelAuthorization: {
          endpoint: `${BASE_URL}api/v1/pusher/auth/`,
          transport: 'ajax',
          headers: {
            'X-CSRFToken': csrfToken,
          },
        },
      });
      const channel = pusher.subscribe(`private-inscribe-user_${user.uuid}`);
      channel.bind('document-state-change', (data) => {
        this.emitter.emit('document-state-change', data);
      });
      channel.bind('customer-fraud-rating-updated', (data) => {
        this.emitter.emit('customer-fraud-rating-updated', data);
      });
      channel.bind('document-review-agent-status-updated', (data) => {
        this.emitter.emit('document-review-agent-status-updated', data);
      });
      channel.bind('customer-review-agent-status-updated', (data) => {
        this.emitter.emit('customer-review-agent-status-updated', data);
      });
      channel.bind('customer-fraud-reasoning-agent-run-updated', (data) => {
        this.emitter.emit('customer-fraud-reasoning-agent-run-updated', data);
      });
      channel.bind('business-compliance-agent-status-updated', (data) => {
        this.emitter.emit('business-compliance-agent-status-updated', data);
      });
      channel.bind('customer-fraud-report-generated', (data) => {
        this.emitter.emit('customer-fraud-report-generated', data);
      });
    },
    showDocumentNotification(document) {
      showDocumentProcessedNotification(
        document.name,
        `/#/customer/${document.customer_id}/document/${document.id}`,
        document.state,
      );
    },
    initializeIntercom(user) {
      if (!user) {
        return;
      }
      this.intercomInitialized = true;
      Intercom({
        app_id: 'c7so6toh',
        user_id: user.id,
        name: `${user.first_name.split(' ').join('\xa0')} ${user.last_name
          .split(' ')
          .join('\xa0')}`,
        email: user.email,
        user_hash: user.intercom_hash,
        company: {
          name: user.organisation.name,
          id: user.organisation.id,
        },
      });
    },
  },
};
</script>
<style scoped>
.app {
  height: 100%;
}
</style>
